import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, PageContent } from "@/components/layout/PageHeader";
import { Users, Eye, TrendingUp, CalendarClock, Euro, Send, FileText, Sparkles, Cpu, HardDrive, Database, Radio, Activity } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area
} from "recharts";

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="bg-slate-900 border border-slate-800 hover:border-slate-700 rounded-md p-5 transition-colors" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-slate-400 font-mono">{label}</span>
        <Icon size={18} className={accent || "text-blue-400"} strokeWidth={1.75} />
      </div>
      <div className="mt-3 text-3xl font-extrabold text-white tracking-tight">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [system, setSystem] = useState(null);
  const [streaming, setStreaming] = useState(null);
  const [streamStatus, setStreamStatus] = useState(null);
  const [growth, setGrowth] = useState([]);
  const [top, setTop] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/stats/overview"),
      api.get("/stats/growth"),
      api.get("/stats/top-posts"),
      api.get("/system/status"),
      api.get("/streaming-manager/overview"),
      api.get("/streams/status"),
    ]).then(([s, g, t, sys, stream, streamState]) => {
      setStats(s.data); setGrowth(g.data); setTop(t.data); setSystem(sys.data); setStreaming(stream.data); setStreamStatus(streamState.data);
    }).catch(() => {});
  }, []);

  const fmt = (n) => new Intl.NumberFormat("it-IT").format(n || 0);

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Dashboard" subtitle="Monitoraggio in tempo reale del tuo ecosistema CalcioHub AI." testid="dashboard-header" />
      <PageContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi testid="kpi-subscribers" icon={Users} label="Iscritti Telegram" value={fmt(stats?.total_subscribers)} />
          <Kpi testid="kpi-views" icon={Eye} label="Visualizzazioni" value={fmt(stats?.total_views)} accent="text-sky-400" />
          <Kpi testid="kpi-growth" icon={TrendingUp} label="Crescita giornaliera" value={`+${fmt(stats?.daily_growth)}`} accent="text-emerald-400" />
          <Kpi testid="kpi-scheduled" icon={CalendarClock} label="Post programmati" value={fmt(stats?.scheduled_count)} accent="text-amber-400" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi testid="kpi-revenue" icon={Euro} label="Entrate pubblicitarie" value={`€ ${fmt(stats?.ad_revenue)}`} accent="text-emerald-400" />
          <Kpi testid="kpi-channels" icon={Send} label="Canali Telegram" value={fmt(stats?.channels_count)} />
          <Kpi testid="kpi-published" icon={FileText} label="Post pubblicati" value={fmt(stats?.published_count)} />
          <Kpi testid="kpi-drafts" icon={Sparkles} label="Bozze" value={fmt(stats?.draft_count)} accent="text-sky-400" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi testid="kpi-cpu" icon={Cpu} label="CPU Backend" value={`${system?.cpu?.percent ?? 0}%`} accent="text-amber-400" />
          <Kpi testid="kpi-ram" icon={HardDrive} label="RAM" value={`${system?.memory?.percent ?? 0}%`} accent="text-emerald-400" />
          <Kpi testid="kpi-mongo" icon={Database} label="MongoDB" value={system?.mongodb?.ok ? "Online" : "Offline"} accent={system?.mongodb?.ok ? "text-emerald-400" : "text-red-400"} />
          <Kpi testid="kpi-streaming" icon={Radio} label="Sorgenti Streaming" value={fmt(streaming?.sources_active)} accent="text-purple-400" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi testid="kpi-managed-streams" icon={Radio} label="Stream gestiti" value={fmt(streamStatus?.total)} accent="text-sky-400" />
          <Kpi testid="kpi-ffmpeg-running" icon={Cpu} label="FFmpeg attivi" value={fmt(streamStatus?.running)} accent="text-emerald-400" />
          <Kpi testid="kpi-stream-errors" icon={HardDrive} label="Errori stream" value={fmt(streamStatus?.errors)} accent={streamStatus?.errors ? "text-red-400" : "text-emerald-400"} />
          <Kpi testid="kpi-stream-processes" icon={Activity} label="Processi stream" value={fmt(streamStatus?.processes)} accent="text-amber-400" />
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-md p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="font-bold text-white">Monitor Server</h3>
              <p className="text-xs text-slate-500 font-mono">Backend, Scheduler, MongoDB, Streaming Manager</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-slate-950 rounded-md p-3 border border-slate-800">
              <div className="text-slate-500 text-xs font-mono">Disco</div>
              <div className="text-white font-bold mt-1">{system?.disk?.percent ?? 0}% usato</div>
            </div>
            <div className="bg-slate-950 rounded-md p-3 border border-slate-800">
              <div className="text-slate-500 text-xs font-mono">Scheduler</div>
              <div className="text-white font-bold mt-1">{system?.scheduler?.running ? "Attivo" : "Non attivo"} · {system?.scheduler?.jobs?.length || 0} job</div>
            </div>
            <div className="bg-slate-950 rounded-md p-3 border border-slate-800">
              <div className="text-slate-500 text-xs font-mono">Streaming Manager</div>
              <div className="text-white font-bold mt-1">
                {fmt(streaming?.sources_with_stream)} sorgenti con URL · {fmt(streaming?.sources_hls)} HLS · {fmt(streamStatus?.running)} FFmpeg attivi
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-md p-5" data-testid="chart-growth">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-white">Crescita iscritti</h3>
                <p className="text-xs text-slate-500 font-mono">ULTIMI 14 GIORNI</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={growth}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #1E293B", borderRadius: 6 }} />
                <Area type="monotone" dataKey="subscribers" stroke="#3B82F6" strokeWidth={2} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-md p-5" data-testid="chart-views">
            <h3 className="font-bold text-white mb-1">Visualizzazioni</h3>
            <p className="text-xs text-slate-500 font-mono mb-4">TREND GIORNALIERO</p>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={growth}>
                <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #1E293B", borderRadius: 6 }} />
                <Line type="monotone" dataKey="views" stroke="#38BDF8" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-md p-5" data-testid="top-posts">
          <h3 className="font-bold text-white mb-4">Post più performanti</h3>
          {top.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">Nessun post pubblicato ancora.</div>
          ) : (
            <div className="space-y-2">
              {top.map((p, i) => (
                <div key={p.id} className="flex items-center gap-4 py-2 border-b border-slate-800 last:border-0">
                  <div className="text-xs font-mono text-slate-500 w-6">#{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-medium truncate">{p.title}</div>
                    <div className="text-xs text-slate-500">{p.tags?.join(", ")}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-blue-400 font-mono">{fmt(p.views)}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">views</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </PageContent>
    </div>
  );
}
